import React from 'react'
import {
  CommunityPost,
  RichTextEditor,
  RichTextEditorHandle,
  Spinner,
  Stack,
} from '@factorialco/f0-react/dist/experimental'
import { useCurrentSession } from 'modules/core/hooks/useCurrentSession'
import { useMutation } from 'modules/core/hooks/useMutation'
import { useQuery } from 'modules/core/hooks/useQuery'
import { handleResult } from 'modules/core/lib/handleMutation'
import i18n from 'modules/core/lib/i18n'
import buildUrl from 'modules/core/lib/buildUrl'
import { trackAddTicketComment } from 'modules/ticketing/lib/tracking'
import {
  AddTicketCommentDocument,
  TicketCommentAuthorsDocument,
  TicketCommentsDocument,
} from 'generated/resources/graphql'
import { useMemo, useRef, useState } from 'react'

export type TicketCommentsProps = {
  ticketId: string
}

export const TicketComments: React.FC<TicketCommentsProps> = ({ ticketId }) => {
  const { company } = useCurrentSession()
  const [newCommentValue, setNewCommentValue] = useState<string | null>(null)
  const commentEditorRef = useRef<RichTextEditorHandle | null>(null)

  const {
    data: commentsData,
    loading: commentsLoading,
    refetch: refetchComments,
  } = useQuery(TicketCommentsDocument, { variables: { ticketId }, skip: !ticketId })

  const comments = useMemo(
    () => commentsData?.ticketing?.ticketCommentsConnection?.nodes ?? [],
    [commentsData]
  )

  const authorEmployeeIds = useMemo(
    () => [...new Set(comments.map((c) => c.authorEmployeeId))],
    [comments]
  )

  const { data: authorsData } = useQuery(TicketCommentAuthorsDocument, {
    variables: { ids: authorEmployeeIds },
    skip: authorEmployeeIds.length === 0,
  })

  const authorsById = useMemo(() => {
    const map = new Map<string, { firstName: string; lastName: string; avatarUrl?: string }>()
    for (const employee of authorsData?.employees?.employeesConnection?.nodes ?? []) {
      map.set(employee.id, {
        firstName: employee.preferredName ?? employee.firstName,
        lastName: employee.lastName,
        avatarUrl: buildUrl(employee.access.avatar) || undefined,
      })
    }
    return map
  }, [authorsData])

  const [performAddComment] = useMutation(AddTicketCommentDocument, {
    extractorFn: (data) => data.ticketingMutations.createTicketingTicketComment,
  })

  const handlePostComment = async () => {
    const body = newCommentValue?.trim() ?? ''
    if (ticketId == null || body === '') return
    const result = await performAddComment({
      variables: {
        ticketId,
        body,
        companyId: company.id,
      },
    })
    const handled = handleResult(result, () => {})
    if (handled.success) {
      trackAddTicketComment({ origin: 'manual' })
      setNewCommentValue(null)
      commentEditorRef.current?.clear()
      await refetchComments()
    }
  }

  return (
    <Stack gap="4" paddingY="p-4">
      <Stack gap="1" style={{ margin: 0, fontWeight: 600 }}>
        {i18n.t('ticketing.inbox.comments.section_title')}
      </Stack>
      {commentsLoading ? (
        <Spinner size="small" />
      ) : (
        <>
          <Stack gap="2">
            {comments.map((comment) => {
              const author = authorsById.get(comment.authorEmployeeId)

              return (
                <CommunityPost
                  key={comment.id}
                  id={`${comment.id}`}
                  title={''}
                  description={comment.body}
                  createdAt={new Date(comment.createdAt)}
                  author={
                    author
                      ? {
                          firstName: author.firstName,
                          lastName: author.lastName,
                          avatarUrl: author.avatarUrl,
                        }
                      : undefined
                  }
                  // TODO: Required by CommunityPost API but unused for ticket comments
                  group={{ title: '', onClick: () => {} }}
                  counters={{ comments: '' }}
                  inLabel={''}
                  comment={{ label: '', onClick: () => {} }}
                  onClick={() => {}}
                  noReactionsButton
                />
              )
            })}
          </Stack>
          <Stack gap="2" style={{ maxWidth: 640 }}>
            <RichTextEditor
              ref={commentEditorRef}
              title={i18n.t('ticketing.inbox.comments.section_title')}
              placeholder={i18n.t('ticketing.inbox.comments.add_comment_placeholder')}
              onChange={(result) => setNewCommentValue(result.value)}
              primaryAction={{
                action: {
                  label: i18n.t('ticketing.inbox.comments.post'),
                  onClick: handlePostComment,
                  variant: 'default',
                  disabled: !(newCommentValue?.trim() ?? ''),
                },
              }}
            />
          </Stack>
        </>
      )}
    </Stack>
  )
}
